import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { shouldReportBoardError } from "../boardFailures";
import { type Board, type UpdateBoardRequest } from "../BoardRecord";
import { type BoardService } from "../BoardService";

import {
  cacheBoardWriteResponse,
  refreshBoardAndInvalidateLists,
} from "./refreshBoardQueries";

/**
 * Sends `updates` for the board and caches the record the server stored
 * (`cacheBoardWriteResponse`), which it resolves with. Throws what the request
 * throws. Whether or not the write succeeded, the board is then refetched and
 * the org's board lists invalidated.
 */
export async function updateBoardAndCache(
  queryClient: QueryClient,
  boardService: BoardService,
  boardId: string,
  orgId: string,
  updates: UpdateBoardRequest,
): Promise<Board> {
  try {
    const record = await boardService.updateBoard(boardId, updates, orgId);
    cacheBoardWriteResponse(queryClient, record);
    return record;
  } finally {
    // The server may have saved the write even when its response was lost,
    // so the board is refetched after a failure too. The refetch ignores its
    // own failures, so it never fails a write the server saved and never
    // sends the update again.
    await refreshBoardAndInvalidateLists(
      queryClient,
      boardService,
      boardId,
      orgId,
    );
  }
}

/**
 * Rename a board or replace its definition, resolving with the record the
 * server stored. Build `updates` with `updateBoardRequestFor`.
 */
export function useUpdateBoard() {
  const { boardService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boardId,
      orgId,
      updates,
    }: {
      boardId: string;
      orgId: string;
      updates: UpdateBoardRequest;
    }) =>
      updateBoardAndCache(queryClient, boardService, boardId, orgId, updates),
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
